import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { StatTile, Card, Pill, ProgressBar, Button, EmptyState } from "../../components/common/Primitives";
import { WelcomeHero, WarmStatTile, SoftSection } from "../../components/warm/WelcomeHero";
import InvestorOnboardingWizard from "../../components/investor/InvestorOnboardingWizard";
import PortfolioSection from "../../components/investor/PortfolioSection";
import PortfolioInsights from "../../components/investor/PortfolioInsights";
import HotDealsSection from "../../components/investor/HotDealsSection";
import InMotionSection from "../../components/investor/InMotionSection";
import CuratedMatchesSection from "../../components/investor/CuratedMatchesSection";
import InvestorCompletenessPopover from "../../components/investor/InvestorCompletenessPopover";
import { Sparkles, ArrowRight, User as UserIcon, Mail, Phone, Flame, Briefcase, Activity, Target, TrendingUp, Heart, Radio, FileSearch, ShieldCheck, Users } from "lucide-react";
import { RevenueSpotlight, ServiceCardTrio } from "../../components/warm/RevenueSpotlight";
import api from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";

export default function InvestorDashboard() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [matches, setMatches] = useState([]);
  const [interests, setInterests] = useState([]);
  const [saved, setSaved] = useState([]);
  const [manager, setManager] = useState(null);
  const [portfolio, setPortfolio] = useState({ items: [], total_investments: 0, total_deployed_inr: 0 });
  const [portfolioInsights, setPortfolioInsights] = useState(null);
  const [hotDeals, setHotDeals] = useState([]);
  const [inMotion, setInMotion] = useState({ deal_rooms: [], open_queries: [], recent_threads: [] });
  const [welcomeOpen, setWelcomeOpen] = useState(false);

  const load = async () => {
    try {
      const [m, mm, ii, ss, mgr, pf, pfi, hd, im] = await Promise.all([
        api.get("/investor/me"),
        api.get("/investor/matches"),
        api.get("/investor/interests"),
        api.get("/investor/saved"),
        api.get("/investor/my-manager"),
        api.get("/investor/portfolio"),
        api.get("/investor/portfolio/insights"),
        api.get("/investor/hot-deals"),
        api.get("/investor/in-motion"),
      ]);
      setMe(m.data);
      setMatches(mm.data || []);
      setInterests(ii.data || []);
      setSaved(ss.data || []);
      setManager(mgr.data?.assigned ? mgr.data.manager : null);
      setPortfolio(pf.data || { items: [], total_investments: 0, total_deployed_inr: 0 });
      setPortfolioInsights(pfi.data || null);
      setHotDeals(hd.data || []);
      setInMotion(im.data || { deal_rooms: [], open_queries: [], recent_threads: [] });
      // Only auto-open the wizard once the regulatory Exchange Declaration has
      // been acknowledged, otherwise the wizard overlay intercepts the
      // declaration's Acknowledge button pointer events.
      if (m.data && m.data.welcome_seen === false) {
        try {
          const ackOk = () => localStorage.getItem("venturaz_exchange_ack_v1") === "1";
          if (ackOk()) {
            setWelcomeOpen(true);
          } else {
            let tries = 0;
            const t = setInterval(() => {
              tries += 1;
              if (ackOk()) { clearInterval(t); setWelcomeOpen(true); }
              else if (tries > 60) { clearInterval(t); }
            }, 1000);
          }
        } catch (e) { setWelcomeOpen(true); }
      }
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/investor/submit-for-approval");
      toast.success("Submitted for approval.");
      load();
    } catch (e) { toast.error("Submission failed"); }
  };

  const status = me?.investor?.status || "Draft";
  const inMotionCount = inMotion.deal_rooms.length + inMotion.open_queries.length + inMotion.recent_threads.length;

  const suggestedActions = useMemo(() => {
    const actions = [];
    const prefs = me?.preferences || {};
    if (!prefs.preferred_sectors || prefs.preferred_sectors.length === 0) {
      actions.push({ to: "/investor/preferences", label: "Set sector preferences", hint: "Sharper matches — 3× more relevant startups." });
    }
    if (!prefs.ticket_size_min || !prefs.ticket_size_max) {
      actions.push({ to: "/investor/preferences", label: "Define ticket range", hint: "Only see deals within your investment size." });
    }
    if ((me?.investor?.kyc_status || "Not Submitted") !== "Approved") {
      actions.push({ to: "/investor/kyc", label: "Complete KYC", hint: "Required to unlock deal rooms." });
    }
    if (hotDeals.length > 0 && interests.length === 0) {
      actions.push({ to: "/investor/marketplace", label: `${hotDeals.length} hot deals live now`, hint: "See what other investors are chasing." });
    }
    return actions.slice(0, 3);
  }, [me, hotDeals, interests]);

  return (
    <DashboardShell
      subtitle={`Welcome back, ${user?.name || user?.email}`}
      title="Investor Cockpit"
      actions={status !== "Approved"
        ? <Button onClick={submit} data-testid="investor-submit-approval">Submit for Approval</Button>
        : <Pill tone="success">Approved</Pill>}
    >
      <Toaster richColors position="top-right" />
      <InvestorOnboardingWizard
        open={welcomeOpen}
        onClose={() => { setWelcomeOpen(false); load(); }}
      />

      <WelcomeHero
        name={user?.name || user?.email}
        eyebrow="INVESTOR COCKPIT"
        subhead={
          status === "Approved"
            ? "Fresh curation, warm intros, and portfolio pulses — all in one calm view."
            : "Almost live. A few more details and the Directory opens up."
        }
        badge={
          <div className="flex flex-col items-end gap-2">
            <Pill tone={status === "Approved" ? "success" : "gold"}>{status}</Pill>
            {portfolio.total_investments > 0 && (
              <Pill tone="gold">₹{formatInr(portfolio.total_deployed_inr || 0)} deployed</Pill>
            )}
          </div>
        }
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="investor-hero-strip">
          <WarmStatTile
            index={0}
            label="Curated Matches"
            value={matches.length}
            icon={Target}
            hint={matches.length > 0 ? "Freshly picked for you" : "Set your preferences"}
            href="/investor/marketplace"
            testId="tile-matches"
          />
          <WarmStatTile
            index={1}
            label="Interests"
            value={interests.length}
            icon={Heart}
            hint={interests.length > 0 ? "Founders you're chasing" : "Browse the Directory"}
            href="/investor/interests"
            testId="tile-interests"
          />
          <WarmStatTile
            index={2}
            label="In Motion"
            value={inMotionCount}
            icon={Radio}
            hint={inMotionCount > 0 ? "Live threads & rooms" : "No live conversations"}
            href="/investor/deal-rooms"
            testId="tile-in-motion"
          />
          <WarmStatTile
            index={3}
            label="Portfolio"
            value={<span data-testid="tile-portfolio-count">{portfolio.total_investments}</span>}
            icon={Briefcase}
            hint={portfolio.total_investments > 0 ? "Companies invested" : "Add your first"}
            href="/investor/track-record"
            testId="tile-portfolio"
          />
        </div>
      </WelcomeHero>

      <SoftSection delay={0.05}>
        <RevenueSpotlight
          eyebrow="INVEST WITH CONVICTION"
          title="Every deck deserves a second opinion — get diligence done right."
          subtitle="Order a Deep DD report on any startup before you write the cheque. Our analyst team compiles the financials, market, team and governance signals into one investable document."
          bullets={[
            "Independent third-party verification",
            "Delivered within 5 business days",
            "Trace every number to source data",
            "Shareable PDF for your IC",
          ]}
          primary={{ href: "/investor/diligence-orders", label: "Order Diligence", price: "From ₹75,000 per report" }}
          secondary={{ href: "/investor/marketplace", label: "Browse the Directory →" }}
        />
      </SoftSection>

      {/* Completeness popover — kept for KYC-critical prompts */}
      <div className="mb-6">
        <InvestorCompletenessPopover me={me} pct={user?.profile_completeness ?? 15} />
      </div>

      {/* Portfolio deployed capital banner (only if any) */}
      {portfolio.total_investments > 0 && (
        <SoftSection delay={0.08}>
          <div className="border border-parchment bg-white/90 rounded-[20px] p-5 mb-6 flex flex-wrap items-center justify-between gap-3 shadow-[0_2px_10px_rgba(15,61,46,0.04)]" data-testid="investor-deployed-banner">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-gold/10 text-gold flex items-center justify-center">
                <Briefcase size={19} strokeWidth={2.4} />
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-ink/50">Capital Deployed</div>
                <div className="font-display text-3xl text-navy-deep" data-testid="investor-deployed-amount">
                  ₹{formatInr(portfolio.total_deployed_inr || 0)}
                </div>
              </div>
            </div>
            <div className="text-sm text-ink/60">
              Across <span className="font-heading text-navy-deep">{portfolio.total_investments}</span> startup{portfolio.total_investments === 1 ? "" : "s"}
            </div>
          </div>
        </SoftSection>
      )}

      <SoftSection delay={0.1}>
        <RevenueSpotlight
          eyebrow="MEMBERSHIP HIGHLIGHT"
          title="Institutional Tier: first look on every Directory launch."
          subtitle="Priority access to A+ startups, a dedicated relationship manager, and every warm intro before it goes public. For investors who want their unfair advantage codified."
          bullets={[
            "Weekly curated dealflow matched to your thesis",
            "Advisor-led warm intros to founders",
            "Dedicated relationship manager",
            "Discounted diligence orders",
          ]}
          primary={{ href: "/pricing", label: "Explore Institutional Tier", price: "From ₹2,99,000 / year" }}
          secondary={{ href: "/investor/preferences", label: "Set thesis preferences →" }}
        />
      </SoftSection>

      <SoftSection delay={0.12}>
        <ServiceCardTrio
          eyebrow="MEMBERSHIP & SERVICES"
          title="Level up your dealflow"
          items={[
            {
              id: "institutional",
              icon: ShieldCheck,
              title: "Institutional Tier",
              description: "Priority access to A+ startups, first-look on Directory launches, dedicated relationship manager.",
              cta: "Explore tiers",
              href: "/pricing",
              priceLabel: "From ₹2,99,000 / year",
            },
            {
              id: "curated-dealflow",
              icon: Target,
              title: "Curated Dealflow",
              description: "Weekly picks matched to your thesis — every Friday, straight in your inbox with founder briefs.",
              cta: "See preferences",
              href: "/investor/preferences",
              priceLabel: "Included with membership",
            },
            {
              id: "warm-intros",
              icon: Users,
              title: "Warm Intros",
              description: "Advisor-led intros to founders — no cold outreach, no ghosting. VenturaZ manages the loop.",
              cta: "How intros work",
              href: "/investor/marketplace",
              priceLabel: "Premium & above",
            },
          ]}
        />
      </SoftSection>

      {/* Suggested Actions row */}
      {suggestedActions.length > 0 && (
        <Card testId="investor-suggested-actions" className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline flex items-center gap-1.5"><Sparkles size={12} className="text-gold" /> Suggested next actions</div>
              <div className="font-heading text-ink text-lg mt-1">Tune your deal flow</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {suggestedActions.map((a, i) => (
              <Link
                key={i}
                to={a.to}
                className="border border-white/10 p-4 rounded-md hover:border-emerald-300 transition-colors group"
                data-testid={`suggested-action-${i}`}
              >
                <div className="text-ink font-heading text-sm flex items-center justify-between">
                  {a.label}
                  <ArrowRight size={14} className="text-emerald-600 group-hover:translate-x-0.5 transition-transform" />
                </div>
                <div className="text-xs text-ink-muted mt-1">{a.hint}</div>
              </Link>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Curated Matches */}
          <CuratedMatchesSection matches={matches} />

          {/* In Motion */}
          <InMotionSection data={inMotion} />

          {/* Portfolio */}
          <PortfolioSection portfolio={portfolio} onChange={load} />
        </div>

        {/* Right rail */}
        <div className="space-y-6">
          {/* Manager card */}
          {manager ? (
            <Card testId="investor-manager-card">
              <div className="overline mb-3">Your VenturaZ Manager</div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#007453] to-[#1795BD] flex items-center justify-center text-white font-heading text-lg">
                  {(manager.name || manager.email).split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-ink text-sm truncate">{manager.name || manager.email}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{manager.role}</div>
                </div>
              </div>
              <div className="mt-4 space-y-2 text-sm">
                <a href={`mailto:${manager.email}`} className="flex items-center gap-2 text-ink-soft hover:text-[#8A6600]" data-testid="investor-manager-email">
                  <Mail size={14} className="text-[#B58900]" /> {manager.email}
                </a>
                {manager.mobile && (
                  <a href={`tel:${manager.mobile}`} className="flex items-center gap-2 text-ink-soft hover:text-[#8A6600]" data-testid="investor-manager-phone">
                    <Phone size={14} className="text-[#B58900]" /> {manager.mobile}
                  </a>
                )}
              </div>
              <div className="mt-4 pt-3 border-t border-navy/10 text-xs text-ink-muted">
                Your dedicated point-of-contact for deal flow, due-diligence and any VenturaZ advisory needs.
              </div>
            </Card>
          ) : (
            <Card testId="investor-manager-empty">
              <div className="overline mb-3">Your VenturaZ Manager</div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-navy/[0.06] flex items-center justify-center text-ink-muted">
                  <UserIcon size={20} />
                </div>
                <div className="text-ink-muted text-sm">Awaiting assignment — a VenturaZ manager will be assigned to you within 24h.</div>
              </div>
            </Card>
          )}

          {/* Hot Deals */}
          <HotDealsSection deals={hotDeals} />

          {/* Portfolio Insights */}
          {portfolio.total_investments > 0 && (
            <PortfolioInsights insights={portfolioInsights} />
          )}

          {/* Interests Expressed */}
          <Card testId="section-active-interests">
            <div className="overline mb-3 flex items-center gap-1.5"><Activity size={12} /> Interests Expressed</div>
            {interests.length === 0 ? (
              <div className="text-ink-muted text-sm">No interests expressed yet. Browse the Directory to start.</div>
            ) : (
              <div className="space-y-2">
                {interests.slice(0, 5).map((i) => (
                  <Link key={i.interest_id} to={`/startup/${i.startup_id}`} className="block border border-white/10 p-3 hover:border-gold/40 transition-colors rounded" data-testid={`interest-row-${i.interest_id}`}>
                    <div className="text-ink font-heading text-sm flex items-center justify-between">
                      {i.startup_name}
                      <ArrowRight size={14} className="text-gold" />
                    </div>
                    <div className="text-xs font-mono text-ink-muted uppercase mt-1">{i.status}</div>
                  </Link>
                ))}
                {interests.length > 5 && (
                  <Link to="/investor/interests" className="block text-center text-xs text-emerald-700 font-heading py-2 hover:underline">
                    See all {interests.length} interests →
                  </Link>
                )}
              </div>
            )}
          </Card>

          {/* Saved marketplace shortcut */}
          {saved.length > 0 && (
            <Card testId="section-saved-shortcut">
              <div className="overline mb-3 flex items-center gap-1.5"><TrendingUp size={12} /> Watchlist</div>
              <div className="text-sm text-ink-soft mb-3">You have <span className="font-heading text-ink">{saved.length}</span> startup{saved.length === 1 ? "" : "s"} saved.</div>
              <Link to="/investor/saved-startups" className="inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:underline font-heading" data-testid="saved-shortcut-cta">
                Open watchlist <ArrowRight size={14} />
              </Link>
            </Card>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}

function formatInr(n) {
  if (!n || Number.isNaN(n)) return "0";
  if (n >= 1e7) return `${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `${(n / 1e5).toFixed(1)} L`;
  return n.toLocaleString("en-IN");
}
